import React, { useState, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import { useSimpleError } from 'hooks';
import { useMutation, useConditionalSubscription } from 'features/common/security/hooks';
import RequiredDocTypeComponent from './RequiredDocTypeComponent';
import { ADD_ATTACHMENTS, COMPUTE_REQUIRED_DOC_TYPES } from '../mutations';
import { REQUIRED_DOC_TYPES_COMPUTED } from '../subscriptions';

const RequiredDocTypeListContainer = ({ documentId, onCloseModal }) => {
  const showError = useSimpleError();
  const [requiredDocTypes, setRequiredDocTypes] = useState([]);
  const [filteredRequiredDocTypes, setFilteredRequiredDocTypes] = useState([]);
  const [adding, setAdding] = useState(false);
  const [filter, setFilter] = useState('');
  const [loading, setLoading] = useState(true);

  const [computeRequiredDocTypes] = useMutation(COMPUTE_REQUIRED_DOC_TYPES, {
    onError: error => {
      showError(error);
      setAdding(false);
    }
  });

  const [addAttachments] = useMutation(ADD_ATTACHMENTS, {
    onError: error => {
      showError(error);
      setAdding(false);
    }
  });

  useEffect(() => {
    computeRequiredDocTypes({
      variables: { input: { documentId } }
    });
  }, [computeRequiredDocTypes, documentId]);

  useEffect(() => {
    setFilteredRequiredDocTypes(
      requiredDocTypes.filter(
        docType =>
          (docType.documentName && docType.documentName.toLowerCase().includes(filter.toLowerCase())) ||
          (docType.contextName && docType.contextName.toLowerCase().includes(filter.toLowerCase()))
      )
    );
  }, [requiredDocTypes, filter]);

  const handleToggleSelectItem = useCallback(
    (id, contextName) => {
      setRequiredDocTypes(
        requiredDocTypes.map(docType =>
          docType.id === id && docType.contextName === contextName ? Object.assign({}, docType, { selected: !docType.selected }) : docType
        )
      );
    },
    [requiredDocTypes]
  );

  const handleAddAttachments = useCallback(() => {
    if (!requiredDocTypes.some(docType => docType.selected)) {
      return;
    }

    setAdding(true);

    const details = requiredDocTypes
      .filter(docType => docType.selected)
      .map(({ id, contextName }) => ({ requiredDocTypeId: id, contextName }));

    addAttachments({
      variables: { input: { documentId, details } }
    });
  }, [addAttachments, documentId, requiredDocTypes]);

  const handleBackDropClick = useCallback(
    (_, reason) => {
      if (reason && reason == 'backdropClick') {
        return;
      }
      onCloseModal();
    },
    [onCloseModal]
  );

  useConditionalSubscription(REQUIRED_DOC_TYPES_COMPUTED, {
    onData: ({
      data: {
        data: { requiredDocTypesComputed }
      }
    }) => {
      setRequiredDocTypes(requiredDocTypesComputed.requiredDocTypes);
      setLoading(false);
    }
  });

  return (
    <RequiredDocTypeComponent
      requiredDocTypes={filteredRequiredDocTypes}
      loading={loading}
      backDropClick={handleBackDropClick}
      adding={adding}
      filter={filter}
      hasSelection={requiredDocTypes.some(docType => docType.selected)}
      onSetFilter={setFilter}
      onCloseModal={onCloseModal}
      onToggleSelectItem={handleToggleSelectItem}
      onAddAttachments={handleAddAttachments}
    />
  );
};

RequiredDocTypeListContainer.propTypes = {
  documentId: PropTypes.number.isRequired,
  onCloseModal: PropTypes.func.isRequired
};

export default RequiredDocTypeListContainer;
